import { h, on, empty } from '../../../../../src/shared/components/dom.js';
import { Chip } from '../../../../../src/shared/components/chip.js';
import { getGenres, getTags, getFilters, getLists, createList } from '../../api.js';
import { navigate } from '../../router.js';
import { mountLiveNowCard } from '../../../../../src/shared/components/live-now-card.js';
import { makeToggle } from '../../components/toggle.js';
import { openListNameModal } from '../../components/list-name-modal.js';
import { state, DEFAULT_FILTERS } from '../../state.js';
import { sanitizeReleaseTypes } from '../../browse-state.js';
import { ReleaseTypeFacet } from '../../components/release-type-facet.js';
import { FACET_LABELS, UNOFFICIAL_TYPES, UNOFFICIAL_GROUP_LABEL } from '../../../../../src/shared/release-type-vocabulary.js';

/**
 * Filter sidebar for the library index.
 *
 * Contains:
 *   - Tag chips (fetched from /api/library/tags — genre, theme, mode, etc.)
 *   - Favorites-only toggle
 *   - Played / Unplayed toggle
 *   - Alphabet quick-nav (A-Z + # for non-alpha)
 *
 * The sidebar doesn't own any state — it reads current filters from the
 * `filters` object and calls `onChange(newFilters)` when the user
 * interacts. The parent (index-screen) merges into catalog state and
 * triggers a reload.
 */

// Alphabet + digits. `#` at the front captures any title that starts
// with a non-alphanumeric character (rare — e.g. "!@#Puzzle"). Digits
// sit after the letters so the usual A-Z scan is uninterrupted at the
// start; many classic ROMs lead with digits (10-Yard Fight, 720°,
// 102 Dalmatians), so they're worth a dedicated lane.
const ALPHABET = [
  '#',
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
  ...'0123456789'.split(''),
];

export function FilterSidebar({ filters, system, onChange, skipLiveNow = false, showMyRoms = false }) {
  const el = h('aside', { class: 'lib-sidebar', 'aria-label': 'Filters' });

  // ── Active filters (shown as removable chips at the top) ──────────
  // This section renders one chip per active filter value. Designed to
  // iterate over normalized arrays, so it handles the future case of
  // multi-select (e.g. { tags: ['puzzle', 'action'] } → two chips).
  const activeFilters = collectActiveFilters(filters, state.get().features?.library_release_types === true);
  if (activeFilters.length > 0) {
    const activeSection = h('div', { class: 'lib-sidebar-section lib-sidebar-active' },
      h('h2', { class: 'lib-sidebar-heading' }, 'Active filters'),
      h('div', { class: 'lib-active-chips' },
        ...activeFilters.map(af =>
          h('button', {
            type: 'button',
            class: 'lib-active-chip',
            title: `Remove filter: ${af.label}`,
            onclick: () => onChange(af.remove(filters)),
          },
            // The kind prefix ("Genre", "Tag", …) is context for ambiguous
            // labels. Saved filters carry a user-authored name that's
            // already self-describing, so collectActiveFilters leaves
            // `kind` null for them and we drop the span entirely. h()
            // skips null children.
            af.kind ? h('span', { class: 'lib-active-chip-kind' }, af.kind) : null,
            h('span', { class: 'lib-active-chip-label' }, af.label),
            h('span', { class: 'lib-active-chip-x', 'aria-hidden': 'true' }, '×'),
          )
        ),
        h('button', {
          type: 'button',
          class: 'lib-btn lib-btn-ghost lib-active-clear-btn',
          onclick: () => onChange({ ...DEFAULT_FILTERS }),
        }, 'Clear all'),
      ),
    );
    el.appendChild(activeSection);
  }

  // ── Genre chips (from games.genres[] — fast, no junction table) ─────
  const genreSection = h('div', { class: 'lib-sidebar-section' },
    h('h2', { class: 'lib-sidebar-heading' }, 'Genre'),
    h('div', { class: 'lib-sidebar-genre-chips', id: 'lib-genre-chips' }),
  );
  el.appendChild(genreSection);
  loadGenres(genreSection.querySelector('#lib-genre-chips'), filters, system, onChange);

  if (state.get().features?.library_release_types === true) {
    el.appendChild(ReleaseTypeFacet({
      selection: filters.release_types,
      onChange: (releaseTypes, focusType) => onChange({ ...filters, release_types: releaseTypes }, focusType),
    }));
  }

  // ── Toggles ────────────────────────────────────────────────────────
  const toggleSection = h('div', { class: 'lib-sidebar-section' },
    h('h2', { class: 'lib-sidebar-heading' }, 'Filter'),
    makeToggle('Top 100', filters.top100, (v) => onChange({ ...filters, top100: v })),
    makeToggle('Favorites only', filters.favorite, (v) => onChange({ ...filters, favorite: v })),
    makeToggle('Played', filters.played === 'true', (v) =>
      onChange({ ...filters, played: v ? 'true' : undefined })),
    makeToggle('Unplayed', filters.played === 'false', (v) =>
      onChange({ ...filters, played: v ? 'false' : undefined })),
    makeToggle('Has achievements', !!filters.achievements, (v) =>
      onChange({ ...filters, achievements: v || undefined })),
    makeToggle('Has high scores', !!filters.scores, (v) =>
      onChange({ ...filters, scores: v || undefined })),
    // "My ROMs" is a capability of the INDEX screen only — the list editor
    // mounts this same sidebar but doesn't forward `mine` to its catalog
    // query, so it must not render a dead toggle (showMyRoms stays false).
    (showMyRoms && state.get().features?.library_my_roms !== false && state.get().user)
      ? makeToggle('My ROMs', !!filters.mine, (v) => onChange({ ...filters, mine: v || false }))
      : null,
  );
  el.appendChild(toggleSection);

  // ── Alphabet quick-nav (A-Z + 0-9 + #) ─────────────────────────────
  // Sits directly under the basic filter toggles — it's a lightweight
  // primary nav, paired with the Filter toggles above, so it lands
  // above the heavier "Custom filters" / "My lists" sections.
  const alphaNav = h('nav', { class: 'lib-sidebar-alpha', 'aria-label': 'Jump to letter' },
    ...ALPHABET.map(letter =>
      h('button', {
        type: 'button',
        class: `lib-alpha-btn${filters.letter === letter ? ' active' : ''}`,
        onclick: () => {
          const next = filters.letter === letter ? '' : letter;
          onChange({ ...filters, letter: next });
        },
      }, letter)
    ),
  );
  el.appendChild(alphaNav);

  // ── Saved filters (Phase 6) — below alphabet so the scan-the-library
  //    nav (letters) comes first and the more advanced custom filters
  //    are tucked underneath ───────────────────────────────────────────
  const savedFiltersSection = h('div', { class: 'lib-sidebar-section' },
    h('h2', { class: 'lib-sidebar-heading' },
      'Custom filters',
      h('a', { class: 'lib-sidebar-heading-link', href: '/library/filters/new', title: 'Create a new filter' }, '+'),
    ),
    h('div', { class: 'lib-sidebar-saved-filters', id: 'lib-saved-filters' }),
  );
  el.appendChild(savedFiltersSection);
  loadSavedFilters(savedFiltersSection.querySelector('#lib-saved-filters'), filters, onChange);

  // ── My lists (Phase 7) — user-curated collections. The `+` opens
  //    an inline prompt to create a new list without leaving the page. ──
  const listsSection = h('div', { class: 'lib-sidebar-section' },
    h('h2', { class: 'lib-sidebar-heading' },
      'My lists',
      h('button', {
        class: 'lib-sidebar-heading-link',
        title: 'Create a new list',
        type: 'button',
        onclick: async () => {
          let error = '';
          let value = '';
          for (;;) {
            const name = await openListNameModal({ title: 'New list', value, error });
            if (!name) return;
            value = name;
            try {
              const created = await createList({ name });
              navigate(`/library/list/${created.id}`);
              return;
            } catch (err) {
              error = err?.body?.error === 'name_taken'
                ? 'You already have a list with that name.'
                : 'Could not create list.';
            }
          }
        },
      }, '+'),
    ),
    h('div', { class: 'lib-sidebar-lists', id: 'lib-sidebar-lists' }),
  );
  el.appendChild(listsSection);
  loadLists(listsSection.querySelector('#lib-sidebar-lists'));

  if (!skipLiveNow) {
    const liveNowSlot = h('div', { class: 'lib-live-now-slot', id: 'lib-live-now' });
    el.appendChild(liveNowSlot);
    mountLiveNowCard(liveNowSlot);
  }

  return el;
}

/**
 * Normalize the filter state into a flat list of {kind, label, remove()}
 * entries for the Active Filters chip row. Iterates over arrays so a
 * future multi-select refactor (e.g. filters.tags = ['puzzle', 'action'])
 * renders multiple chips without code changes here.
 */
function collectActiveFilters(f, showReleaseTypes = false) {
  const out = [];

  if (showReleaseTypes) {
    const selected = sanitizeReleaseTypes(f.release_types);
    if (selected.length !== 1 || selected[0] !== 'official') {
      const label = selected.length === UNOFFICIAL_TYPES.length &&
        UNOFFICIAL_TYPES.every(type => selected.includes(type))
        ? UNOFFICIAL_GROUP_LABEL
        : selected.map(type => FACET_LABELS[type]).join(', ') || 'None';
      out.push({
        kind: 'Release type', label,
        remove: cur => ({ ...cur, release_types: [...DEFAULT_FILTERS.release_types] }),
      });
    }
  }

  // Genre (currently single-value; could become array later)
  const genres = Array.isArray(f.genre) ? f.genre : (f.genre ? [f.genre] : []);
  for (const g of genres) {
    out.push({
      kind: 'Genre',
      label: g,
      remove: (cur) => {
        if (Array.isArray(cur.genre)) {
          return { ...cur, genre: cur.genre.filter(x => x !== g) };
        }
        return { ...cur, genre: '' };
      },
    });
  }

  // Tag (single-value today; future-proofed the same way)
  const tags = Array.isArray(f.tag) ? f.tag : (f.tag ? [f.tag] : []);
  for (const t of tags) {
    out.push({
      kind: 'Tag',
      label: t,
      remove: (cur) => {
        if (Array.isArray(cur.tag)) {
          return { ...cur, tag: cur.tag.filter(x => x !== t) };
        }
        return { ...cur, tag: '' };
      },
    });
  }

  if (f.top100) {
    out.push({ kind: 'Filter', label: 'Top 100', remove: (cur) => ({ ...cur, top100: false }) });
  }
  if (f.favorite) {
    out.push({ kind: 'Filter', label: 'Favorites only', remove: (cur) => ({ ...cur, favorite: false }) });
  }
  if (f.played === 'true') {
    out.push({ kind: 'Filter', label: 'Played', remove: (cur) => ({ ...cur, played: undefined }) });
  }
  if (f.played === 'false') {
    out.push({ kind: 'Filter', label: 'Unplayed', remove: (cur) => ({ ...cur, played: undefined }) });
  }
  if (f.achievements) {
    out.push({ kind: 'Filter', label: 'Has achievements', remove: (cur) => ({ ...cur, achievements: undefined }) });
  }
  if (f.scores) {
    out.push({ kind: 'Filter', label: 'Has high scores', remove: (cur) => ({ ...cur, scores: undefined }) });
  }
  if (f.mine) {
    out.push({ kind: 'Filter', label: 'My ROMs', remove: (cur) => ({ ...cur, mine: false }) });
  }
  if (f.letter) {
    out.push({ kind: 'A–Z', label: f.letter, remove: (cur) => ({ ...cur, letter: '' }) });
  }
  if (f.filter_id) {
    // `_filter_name` is stashed into the filter state at the moment
    // the user activates a saved filter (see loadSavedFilters), and
    // also back-filled on page load if they arrived via URL. We fall
    // back to "#N" until the async fetch resolves — short-lived, a
    // single render cycle.
    const rawName = f._filter_name || `#${f.filter_id}`;
    out.push({
      // No kind prefix — the user-authored name stands on its own.
      // The chip render drops the kind span when null.
      kind: null,
      label: truncate(rawName, 20),
      // Clear `_filter_name` alongside `filter_id` so a later
      // activation of a different saved filter can't inherit the
      // stale name.
      remove: (cur) => ({ ...cur, filter_id: null, _filter_name: null }),
    });
  }

  return out;
}

/**
 * Trim a display label to `max` chars, ellipsising with a single-char
 * horizontal ellipsis. Returns the input unchanged when it fits.
 */
function truncate(s, max) {
  if (!s || s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

async function loadSavedFilters(container, filters, onChange) {
  try {
    const saved = await getFilters();
    empty(container);
    if (!saved.length) {
      container.appendChild(
        h('a', { class: 'lib-sidebar-empty lib-sidebar-empty-link', href: '/library/filters/new' },
          'No saved filters yet · ',
          h('span', { class: 'lib-link' }, 'create one →'))
      );
      return;
    }

    // Back-fill the active filter's name if it's missing from the
    // state (e.g. page loaded via URL with filter_id=3). Fires one
    // extra onChange which re-renders the sidebar with the name
    // populated — the Active Filters chip then shows it instead of
    // the "#3" fallback. Guarded so it only triggers when there's
    // actually a mismatch.
    if (filters.filter_id && !filters._filter_name) {
      const match = saved.find(x => x.id === filters.filter_id);
      if (match) {
        onChange({ ...filters, _filter_name: match.name });
        // Continue rendering — onChange will trigger a re-render with
        // the enriched state, but we still want this pass to finish.
      }
    }

    for (const f of saved) {
      const active = filters.filter_id === f.id;
      const row = h('div', { class: `lib-saved-filter-row${active ? ' active' : ''}` },
        h('button', {
          type: 'button',
          class: 'lib-saved-filter-btn',
          title: `Apply "${f.name}"`,
          onclick: () => {
            const isActivating = filters.filter_id !== f.id;
            // Stash the name alongside the id so the active-filters
            // chip can render it without another round-trip. Clear it
            // on deactivation so the chip doesn't hang around stale.
            onChange({
              ...filters,
              filter_id: isActivating ? f.id : null,
              _filter_name: isActivating ? f.name : null,
            });
          },
        }, f.name),
        h('a', {
          class: 'lib-saved-filter-edit',
          href: `/library/filters/${f.id}`,
          title: 'Edit filter',
        }, '✎'),
      );
      container.appendChild(row);
    }
  } catch (err) {
    // Most likely 401 (not signed in). Show a subtle sign-in hint.
    empty(container);
    if (err?.status === 401) {
      container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'Sign in to save filters'));
    } else {
      container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'Filters unavailable'));
    }
  }
}

/**
 * Load and render the user's game lists (Phase 7). Each row links to
 * /library/list/:id with the list name + item count. The `+` button
 * in the heading creates a new list via a simple prompt — full rename
 * / edit happens on the list detail page.
 *
 * 401 (not signed in) shows a muted "Sign in to create lists" stub,
 * matching the saved-filters section below.
 */
// Module-level handle to the active `library:lists-changed` listener so
// a re-render of the sidebar (which spins up a new loadLists call) can
// replace the previous binding without leaking listeners across
// sessions. Only one sidebar exists at a time, so one handle is fine.
let _listsChangedHandler = null;

async function loadLists(container) {
  // Re-bind the refresh listener on every load. Any code path that
  // mutates list membership (add-to-list modal, remove from a list's
  // detail page, list delete) fires `library:lists-changed` on
  // document, and this handler re-fetches so the item-count pill and
  // the list of names stays current without manual coordination.
  if (_listsChangedHandler) {
    document.removeEventListener('library:lists-changed', _listsChangedHandler);
  }
  _listsChangedHandler = () => { loadLists(container); };
  document.addEventListener('library:lists-changed', _listsChangedHandler);

  try {
    const lists = await getLists();
    empty(container);
    if (!lists.length) {
      container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'No lists yet'));
      return;
    }
    for (const l of lists) {
      const row = h('a', {
        class: 'lib-saved-filter-row',
        href: `/library/list/${l.id}`,
        title: l.description || l.name,
      },
        h('span', { class: 'lib-saved-filter-btn' }, l.name),
        h('span', { class: 'lib-sidebar-list-count' }, String(l.item_count || 0)),
      );
      container.appendChild(row);
    }
  } catch (err) {
    empty(container);
    if (err?.status === 401) {
      container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'Sign in to create lists'));
    } else {
      container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'Lists unavailable'));
    }
  }
}

async function loadGenres(container, filters, system, onChange) {
  const INITIAL = 15;
  try {
    const genres = await getGenres({ system: system !== 'all' ? system : undefined });
    empty(container);

    function renderChips(list) {
      empty(container);
      for (const { genre, count } of list) {
        container.appendChild(Chip({
          label: genre,
          count,
          active: filters.genre === genre,
          onClick: () => {
            const next = filters.genre === genre ? '' : genre;
            onChange({ ...filters, genre: next });
          },
        }));
      }
      return list.length;
    }

    const PAGE = 20;
    let visible = INITIAL;
    const shown = renderChips(genres.slice(0, visible));

    function appendToggle() {
      const remaining = genres.length - visible;
      if (remaining <= 0) return;
      const toggle = h('button', {
        type: 'button',
        class: 'lib-sidebar-show-more',
      }, `Show ${Math.min(PAGE, remaining)} more`);
      toggle.addEventListener('click', () => {
        toggle.remove();
        const next = Math.min(visible + PAGE, genres.length);
        for (const { genre, count } of genres.slice(visible, next)) {
          container.appendChild(Chip({
            label: genre,
            count,
            active: filters.genre === genre,
            onClick: () => {
              const nx = filters.genre === genre ? '' : genre;
              onChange({ ...filters, genre: nx });
            },
          }));
        }
        visible = next;
        appendToggle();
      });
      container.appendChild(toggle);
    }
    appendToggle();
    if (!shown) {
      container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'No genre data yet'));
    }
  } catch {
    container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'Genres unavailable'));
  }
}

async function loadTags(container, filters, system, onChange) {
  try {
    const allTags = await getTags({
      system: system !== 'all' ? system : undefined,
    });
    // Exclude genre-kind tags (they have their own section above).
    // Show top 25 non-genre tags. When there are no non-genre tags
    // yet (Phase 5 starts with genre-only seeds), hide the section.
    const nonGenre = allTags.filter(t => t.kind !== 'genre');
    const top = nonGenre.slice(0, 25);
    empty(container);
    for (const { slug, label, count } of top) {
      container.appendChild(Chip({
        label,
        count,
        active: filters.tag === slug,
        onClick: () => {
          const next = filters.tag === slug ? '' : slug;
          onChange({ ...filters, tag: next });
        },
      }));
    }
    if (!top.length) {
      container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'No tags yet'));
    }
  } catch {
    container.appendChild(h('span', { class: 'lib-sidebar-empty' }, 'Tags unavailable'));
  }
}
